/**
 * Token Usage Store
 *
 * Manages token usage UI state: filters (date range, sortBy, groupBy),
 * forecast preferences, quick filter selection, and modal state.
 * Persisted so filters survive page refresh and in-app navigation.
 *
 * @module store/tokenUsage/tokenUsageStore
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import logger from '../../shared/services/logging/logger';
import { createPersistConfig } from '../middleware/persistenceMiddleware';
import { getDefaultDateRange } from '../../features/tokenUsage/utils/dateFormatters';

const defaultRange = getDefaultDateRange();

export const useTokenUsageStore = create(
  persist(
    (set) => ({
      // Filter state
      startDate: defaultRange.startDate,
      endDate: defaultRange.endDate,
      sortBy: 'cost',
      groupBy: 'day',
      limit: null,

      // UI state
      forecastEnabled: false,
      forecastDays: 7,
      activeQuickFilter: 'this-month',

      // Modal state (not persisted)
      selectedUserId: null,
      showUserDetailsModal: false,

      // ── Filter actions ──────────────────────────────────────

      updateFilters: (newFilters) => {
        logger.debug('Updating token usage filters', { newFilters });
        set(state => ({ ...state, ...newFilters }));
      },

      updateDateRange: (startDate, endDate) => {
        logger.debug('Updating token usage date range', { startDate, endDate });
        set({ startDate, endDate, forecastEnabled: false });
      },

      updateSortBy: (sortBy) => {
        logger.debug('Updating token usage sortBy', { sortBy });
        set({ sortBy });
      },

      updateGroupBy: (groupBy) => {
        logger.debug('Updating token usage groupBy', { groupBy });
        set({ groupBy });
      },

      updateLimit: (limit) => {
        logger.debug('Updating token usage limit', { limit });
        set({ limit });
      },

      resetFilters: () => {
        const range = getDefaultDateRange();
        logger.debug('Resetting token usage filters to defaults');
        set({
          startDate: range.startDate,
          endDate: range.endDate,
          sortBy: 'cost',
          groupBy: 'day',
          limit: null,
          forecastEnabled: false,
          forecastDays: 7,
          activeQuickFilter: 'this-month'
        });
      },

      // ── UI actions ──────────────────────────────────────────

      toggleForecast: () => {
        set(state => ({ forecastEnabled: !state.forecastEnabled }));
      },

      setForecastEnabled: (forecastEnabled) => {
        set({ forecastEnabled });
      },

      setForecastDays: (forecastDays) => {
        set({ forecastDays });
      },

      setActiveQuickFilter: (activeQuickFilter) => {
        set({ activeQuickFilter });
      },

      // ── Modal actions ───────────────────────────────────────

      openUserDetails: (userId) => {
        set({ selectedUserId: userId, showUserDetailsModal: true });
      },

      closeUserDetails: () => {
        set({ selectedUserId: null, showUserDetailsModal: false });
      }
    }),
    createPersistConfig('tokenUsageState', {
      partialize: state => ({
        startDate: state.startDate,
        endDate: state.endDate,
        sortBy: state.sortBy,
        groupBy: state.groupBy,
        limit: state.limit,
        forecastEnabled: state.forecastEnabled,
        forecastDays: state.forecastDays,
        activeQuickFilter: state.activeQuickFilter
        // selectedUserId and showUserDetailsModal intentionally excluded
      })
    })
  )
);

// ── Granular selector hooks ─────────────────────────────────

export const useTokenStartDate = () =>
  useTokenUsageStore(state => state.startDate);

export const useTokenEndDate = () =>
  useTokenUsageStore(state => state.endDate);

export const useTokenSortBy = () =>
  useTokenUsageStore(state => state.sortBy);

export const useTokenGroupBy = () =>
  useTokenUsageStore(state => state.groupBy);

export const useTokenLimit = () =>
  useTokenUsageStore(state => state.limit);

export const useForecastEnabled = () =>
  useTokenUsageStore(state => state.forecastEnabled);

export const useForecastDays = () =>
  useTokenUsageStore(state => state.forecastDays);

export const useActiveQuickFilter = () =>
  useTokenUsageStore(state => state.activeQuickFilter);

export const useSelectedUserId = () =>
  useTokenUsageStore(state => state.selectedUserId);

export const useShowUserDetailsModal = () =>
  useTokenUsageStore(state => state.showUserDetailsModal);

// ── Composite selectors ─────────────────────────────────────

export const useTokenUsageFiltersFromStore = () =>
  useTokenUsageStore(
    useShallow(state => ({
      startDate: state.startDate,
      endDate: state.endDate,
      sortBy: state.sortBy,
      groupBy: state.groupBy,
      limit: state.limit
    }))
  );

// ── Actions hook ────────────────────────────────────────────

export const useTokenUsageActions = () =>
  useTokenUsageStore(
    useShallow(state => ({
      updateFilters: state.updateFilters,
      updateDateRange: state.updateDateRange,
      updateSortBy: state.updateSortBy,
      updateGroupBy: state.updateGroupBy,
      updateLimit: state.updateLimit,
      resetFilters: state.resetFilters,
      toggleForecast: state.toggleForecast,
      setForecastEnabled: state.setForecastEnabled,
      setForecastDays: state.setForecastDays,
      setActiveQuickFilter: state.setActiveQuickFilter,
      openUserDetails: state.openUserDetails,
      closeUserDetails: state.closeUserDetails
    }))
  );
